import { Box, Typography, styled } from '@mui/material';
import i18n from 'i18n';

import { flexMixin } from 'utils/flexMixin';

import { titleTextColor } from 'assets/styles/_palette';

import { StatusBoxType } from './helpers';

export const Documents = styled(Box)(({ theme }) => ({
	...flexMixin({ justify: 'flex-start', direction: 'column', gap: 20 }),
	width: '100%',
	maxWidth: '100%',
	height: '100%',
	boxSizing: 'border-box',
	overflow: 'visible',
	[theme.breakpoints.down('sm')]: {
		gap: 16,
		padding: '0 8px',
	},
	[theme.breakpoints.down('xs')]: {
		gap: 12,
		padding: '0 4px',
	},
}));

export const DocumentTitle = styled(Typography)({
	fontSize: '16px',
	fontWeight: '700',
	lineHeight: ' 157%',
	letterSpacing: '0.1px',
	color: titleTextColor,
});

export const DocumentsElements = styled(Box)({
	...flexMixin({ direction: 'column', gap: 15 }),
	width: '100%',
});

export const DocumentElement = styled(Box)({
	...flexMixin({ justify: 'space-between' }),
	borderRadius: '8px',
	background: 'rgba(236, 243, 255, 0.50)',
	padding: '16px',
	width: '100%',
	boxSizing: 'border-box',
});

export const DocumentInfo = styled(Box)({
	...flexMixin({ gap: 15 }),
	'& p': {
		fontSize: '14px',
		fontWeight: '400',
		lineHeight: ' 143%',
		letterSpacing: '0.15px',
		color: titleTextColor,
	},

	'& svg': {
		height: '40px',
		width: '40px',
	},
});

export const DocumentButtons = styled(Box)({
	...flexMixin({ justify: 'space-between' }),
	'& svg': {
		width: '20px',
		height: '20px',
	},
});

export const FileName = styled(Box)(({ theme }) => ({
	...flexMixin({ justify: 'flex-start', gap: 16 }),

	'& svg': {
		height: '24px',
		width: '24px',
		flexShrink: 0,
	},

	'& p': {
		fontSize: '14px',
		fontWeight: '400',
		lineHeight: '143%',
		letterSpacing: '0.15px',
		color: titleTextColor,
	},
	[theme.breakpoints.down('sm')]: {
		gap: 8,
		'& svg': {
			height: '20px',
			width: '20px',
		},
		'& p': {
			fontSize: '13px',
		},
	},
}));

export const FileDate = styled(Typography)({
	fontSize: '14px',
	fontWeight: '400',
	lineHeight: '143%',
	letterSpacing: '0.15px',
	color: '#58647B',
});

export const Owner = styled(Box)(({ theme }) => ({
	...flexMixin({ justify: 'flex-start', gap: 8 }),
	[theme.breakpoints.down('sm')]: {
		gap: 6,
	},
}));

export const OwnerInfo = styled(Box)(({ theme }) => ({
	...flexMixin({ align: 'flex-start', direction: 'column' }),

	'& h4, & p': {
		fontSize: '14px',
		fontWeight: '400',
		lineHeight: '143%',
		letterSpacing: '0.15px',
		color: titleTextColor,
	},
	'& p': {
		color: '#9FA6B3',
	},
	[theme.breakpoints.down('sm')]: {
		'& h4, & p': {
			fontSize: '12px',
		},
	},
}));

// Styles pour l'affichage mobile en mode carte
export const MobileCardList = styled(Box)(({ theme }) => ({
	display: 'none',
	[theme.breakpoints.down('md')]: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		width: '100%',
	},
}));

export const MobileCard = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: 12,
	padding: 16,
	borderRadius: 12,
	backgroundColor: '#F8FAFC',
	border: '1px solid #E2E8F0',
	width: '100%',
	boxSizing: 'border-box',
	[theme.breakpoints.down('sm')]: {
		padding: 12,
		gap: 10,
	},
}));

export const MobileCardHeader = styled(Box)({
	...flexMixin({ justify: 'space-between', align: 'flex-start' }),
	width: '100%',
});

export const MobileCardRow = styled(Box)({
	...flexMixin({ justify: 'space-between', align: 'center' }),
	width: '100%',
});

export const MobileCardLabel = styled(Typography)({
	fontSize: '12px',
	fontWeight: '500',
	color: '#64748B',
	textTransform: 'uppercase',
	letterSpacing: '0.5px',
});

export const TableWrapper = styled(Box)(({ theme }) => ({
	width: '100%',
	[theme.breakpoints.down('md')]: {
		display: 'none',
	},
}));

export const EmptyState = styled(Box)({
	...flexMixin({ direction: 'column' }),
	width: '100%',
	height: '100%',
	marginBottom: '120px',

	'& svg': {
		width: '48px',
		height: '48px',
	},

	'& h3': {
		fontSize: '20px',
		fontWeight: 700,
		lineHeight: '160%',
		letterSpacing: '0.15px',
		color: '#0E1B33',
		margin: '24px 0 8px 0',
	},

	'& p': {
		fontSize: '16px',
		fontWeight: 400,
		lineHeight: '150%',
		letterSpacing: '0.15px',
		color: '#0E1B33',
	},
});

export const LoaderBox = styled(Box)({
	...flexMixin({}),
	height: '100%',
	width: '100%',
	marginBottom: '160px',
});

export const StatusBox = styled(Box)<StatusBoxType>(({ type }) => ({
	...flexMixin({ gap: 5 }),
	padding: '4px 12px',
	borderRadius: '24px',
	backgroundColor:
		type === 'ready'
			? 'rgb(219, 249, 232)'
			: type === 'notReady'
			? 'rgb(252, 237, 219)'
			: type === 'processing'
			? 'rgb(231, 238, 255)'
			: 'rgb(231, 238, 255)',
	cursor: type === 'notReady' ? 'pointer' : 'unset',
	maxWidth: '150px',
	boxSizing: 'border-box',

	fontSize: '14px',
	fontWeight: 400,
	lineHeight: '150%',
	letterSpacing: '0.15px',
	color:
		type === 'ready'
			? 'rgb(1, 200, 96)'
			: type === 'notReady'
			? 'rgb(241, 151, 2)'
			: type === 'processing'
			? 'rgb(71, 134, 255)'
			: 'rgb(71, 134, 255)',
}));

export const StatusField = styled(Box)({
	...flexMixin({ justify: i18n?.language === 'ar' ? 'flex-start' : 'flex-end' }),
	width: '100%',
});
